import React, { useState, useEffect } from 'react';
import CampaignTrackingList from './CampaignTrackingList';
import CampaignTrackingForm from './CampaignTrackingForm';
import CampaignTrackingHistory from './CampaignTrackingHistory';
import CampaignTrackingQuickActionModal from './CampaignTrackingQuickActionModal';
import { loadCampaignTracking, createCampaignTracking, updateCampaignTracking, deleteCampaignTracking } from '../api/index.js';

const CampaignTrackingSection = ({ currentUser, permissions }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [quickActionCampaign, setQuickActionCampaign] = useState(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await loadCampaignTracking();
      setCampaigns(data);
    } catch (error) {
      console.error('Error al cargar campañas:', error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar campañas por células usando React.useMemo
  const filteredCampaigns = React.useMemo(() => {
    console.log('🔄 CampaignTrackingSection - Iniciando filtrado de campañas...');
    console.log('👤 Usuario actual:', currentUser?.firstName, currentUser?.lastName, 'Rol:', currentUser?.role);
    console.log('📊 Total de campañas recibidas:', campaigns.length);
    
    let currentFiltered = [...campaigns];

    // FILTRAR CAMPAÑAS POR CÉLULAS PARA TODOS LOS ROLES EXCEPTO ADMINISTRADOR
    if (currentUser?.role !== 'admin' && currentUser?.cells && currentUser.cells.length > 0) {
      console.log('🔒 Aplicando filtro de células para usuario no administrador...');
      console.log('🏢 Células permitidas para el usuario:', currentUser.cells);
      
      const beforeCellFilter = currentFiltered.length;
      
      currentFiltered = currentFiltered.filter(campaign => {
        // Verificar si la célula de la campaña está permitida
        const campaignCell = campaign.celula || 'Sin célula';
        const isAllowed = currentUser.cells.includes(campaignCell);
        
        if (!isAllowed) {
          console.log(`❌ Campaña filtrada:`, {
            campaignId: campaign.id,
            campaignCell: campaignCell,
            userCells: currentUser.cells
          });
        } else {
          console.log(`✅ Campaña permitida:`, {
            campaignId: campaign.id,
            campaignCell: campaignCell
          });
        }
        
        return isAllowed;
      });
      
      console.log(`✅ Campañas después del filtro por células: ${currentFiltered.length} (filtradas: ${beforeCellFilter - currentFiltered.length})`);
    } else {
      console.log('👑 Usuario administrador o sin células - sin filtro de células');
    }

    console.log('✅ Campañas finales después de filtros:', currentFiltered.length);
    return currentFiltered;
  }, [campaigns, currentUser]);

  const handleSaveCampaign = async (campaignData) => {
    try {
      if (editingCampaign) {
        // Actualizar campaña existente
        await updateCampaignTracking(editingCampaign.id, campaignData);
        await loadCampaigns();
        setShowForm(false);
        setEditingCampaign(null);
        alert('✅ Campaña actualizada exitosamente');
      } else {
        // Crear nueva campaña
        await createCampaignTracking(campaignData);
        await loadCampaigns();
        setShowForm(false);
        alert('✅ Campaña creada exitosamente');
      }
    } catch (error) {
      console.error('Error al guardar campaña:', error);
      alert('❌ Error al guardar la campaña: ' + error.message);
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setShowForm(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta campaña?')) {
      try {
        await deleteCampaignTracking(campaignId);
        await loadCampaigns();
      } catch (error) {
        console.error('Error al eliminar campaña:', error);
        alert('Error al eliminar la campaña');
      }
    }
  };

  const handleShowHistory = (campaign) => {
    setSelectedCampaign(campaign);
    setShowHistory(true);
  };

  const handleQuickAction = (campaign) => {
    setQuickActionCampaign(campaign);
    setShowQuickAction(true);
  };

  const handleQuickActionSuccess = () => {
    // Recargar la lista después de una acción rápida exitosa
    loadCampaigns();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg text-gray-600">Cargando seguimiento de campañas...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">📊 Seguimiento de Campañas Nuevas</h1>
            <p className="text-purple-100 mt-2">
              Gestiona y da seguimiento a las campañas publicitarias nuevas
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{filteredCampaigns.length}</div>
            <div className="text-purple-100">Campañas Activas</div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-4">
          <button
            onClick={() => {
              setEditingCampaign(null);
              setShowForm(true);
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
          >
            <span className="mr-2">➕</span>
            Nueva Campaña
          </button>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Pendiente</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
            <span className="text-sm text-gray-600">En Proceso</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Completado</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Cancelado</span>
          </div>
        </div>
      </div>

      {/* Campaign List */}
      <CampaignTrackingList
        campaigns={filteredCampaigns}
        onEdit={handleEditCampaign}
        onDelete={handleDeleteCampaign}
        onShowHistory={handleShowHistory}
        onQuickAction={handleQuickAction}
        currentUser={currentUser}
        permissions={permissions}
      />

      {/* Form Modal */}
      {showForm && (
        <CampaignTrackingForm
          campaign={editingCampaign}
          onSave={handleSaveCampaign}
          onClose={() => {
            setShowForm(false);
            setEditingCampaign(null);
          }}
          currentUser={currentUser}
        />
      )}

      {/* History Modal */}
      {showHistory && selectedCampaign && (
        <CampaignTrackingHistory
          campaignId={selectedCampaign.id}
          campaignName={`${selectedCampaign.nombre_cliente} - ${selectedCampaign.campana}`}
          campaign={selectedCampaign}
          onClose={() => {
            setShowHistory(false);
            setSelectedCampaign(null);
          }}
        />
      )}

      {/* Quick Action Modal */}
      {showQuickAction && quickActionCampaign && (
        <CampaignTrackingQuickActionModal
          campaign={quickActionCampaign}
          currentUser={currentUser}
          onClose={() => {
            setShowQuickAction(false);
            setQuickActionCampaign(null);
          }}
          onSuccess={handleQuickActionSuccess}
        />
      )}
    </div>
  );
};

export default CampaignTrackingSection;
